// src/schedule/EmployeeViewDays.js
import React from "react";
import DayRow from "./DayRow";
import FreeDayRow from "./FreeDayRow";

const nameOf = (s) => (typeof s === "object" ? s?.name : s);

export default function EmployeeViewDays({
    filteredDates,              // pełny miesiąc (np. monthDatesFull)
    todayStr,
    eventsByDate,               // pełne dane (oba kalendarze)
    selectedEmployee,
    employees,
    updateEventStaff,
    updateEventColor,
    showColorPicker,
    setShowColorPicker,
    unavailableByDate,
    toggleUnavailable,
    maxWidth,
    isAdmin,
    copyStaffToAllDates,
    updateNeededForEvent,
    rowRefs,
    getAssignedNamesForDate,
    getUnavailableNamesForDate,
    activeCalendarId,           // ignorowane w tym widoku
    allEventsForDate,
    eventsByDateFull,
    dragScale = 1,
    // mobile: obsługa powiększania pojedynczego dnia
    expandedDay,
    onDayClick,
    onCloseExpanded,
    isPortalView = false,
}) {
    // ✅ hook zawsze wywołany – bez warunków
    const internalRowRefs = React.useRef({});
    const rowRefsToUse = rowRefs ?? internalRowRefs;

    const safeGetAssigned =
        typeof getAssignedNamesForDate === "function" ? getAssignedNamesForDate : () => new Set();
    const safeGetUnavailable =
        typeof getUnavailableNamesForDate === "function" ? getUnavailableNamesForDate : () => new Set();

    const selName =
        typeof selectedEmployee === "string" ? selectedEmployee : selectedEmployee?.name;

    // Daty tylko z wydarzeniami tej osoby — logika inline (bez zależności od worksOnDate)
    const employeeDates = React.useMemo(() => {
        const out = [];
        for (const dateStr of filteredDates) {
            const evts = eventsByDate[dateStr] || [];
            if (evts.some((ev) => (ev.staff || []).some((s) => nameOf(s) === selName))) {
                out.push(dateStr);
            }
        }
        return out.sort();
    }, [filteredDates, eventsByDate, selName]);

    // Stabilne kolumny dla tej osoby (kontynuacja po dniu)
    const employeeColumnAssignments = React.useMemo(() => {
        const res = {};
        const colMap = {};
        const lastSeenDate = {};

        const byDate = {};
        employeeDates.forEach((date) => {
            const list = (eventsByDate[date] || []).filter((ev) =>
                (ev.staff || []).some((s) => nameOf(s) === selName)
            );
            byDate[date] = list;
        });

        employeeDates.forEach((date) => {
            const dayEvents = byDate[date] || [];
            const usedColsToday = new Set();

            // kontynuacje (event+calendarId dzień po dniu)
            dayEvents.forEach((evt) => {
                const nameKey = `${(evt.event || "").trim().toLowerCase()}|${evt.calendarId || ""}`;
                const prevDate = lastSeenDate[nameKey];
                const isContinuation =
                    prevDate && new Date(date) - new Date(prevDate) === 24 * 60 * 60 * 1000;
                if (colMap[nameKey] !== undefined && isContinuation) {
                    usedColsToday.add(colMap[nameKey]);
                }
            });

            // przypisanie kolumn
            dayEvents.forEach((evt) => {
                const nameKey = `${(evt.event || "").trim().toLowerCase()}|${evt.calendarId || ""}`;
                const prevDate = lastSeenDate[nameKey];
                const isContinuation =
                    prevDate && new Date(date) - new Date(prevDate) === 24 * 60 * 60 * 1000;

                if (colMap[nameKey] === undefined || !isContinuation) {
                    let col = 0;
                    while (usedColsToday.has(col)) col++;
                    colMap[nameKey] = col;
                    usedColsToday.add(col);
                }

                res[`${date}::${evt.event}::${evt.calendarId || "noCal"}`] = colMap[nameKey];
                lastSeenDate[nameKey] = date;
            });

            // placeholdery - tylko dla faktycznie użytych kolumn w tym dniu
            // Znajdź maksymalną używaną kolumnę w tym dniu
            const maxColToday = Math.max(
                ...Array.from(usedColsToday),
                -1  // -1 jeśli brak kolumn, żeby Math.max zwrócił -1
            );
            const actualMaxCol = maxColToday >= 0 ? maxColToday : -1;
            
            // Twórz placeholdery tylko do rzeczywistej maksymalnej kolumny
            for (let col = 0; col <= actualMaxCol; col++) {
                if (!usedColsToday.has(col)) {
                    res[`${date}::__placeholder__::${col}`] = col;
                }
            }
        });

        return res;
    }, [employeeDates, eventsByDate, selName]);

    // Zapis staffu z właściwym calendarId (bierzemy z dopasowanego eventu)
    // WAŻNE: Przekazujemy pełny obiekt eventu (z gId), aby obsłużyć zmiany nazwy
    const updateWithCalendarId = (date, eventName, newStaff) => {
        const full = eventsByDate[date] || [];
        let match = full.find(
            (ev) => ev.event === eventName && (ev.staff || []).some((s) => nameOf(s) === selName)
        );
        if (!match) match = full.find((ev) => ev.event === eventName);
        const calId = match?.calendarId || null;

        if (typeof updateEventStaff === "function") {
            // Przekaż pełny obiekt eventu (z gId), jeśli jest dostępny, aby obsłużyć zmiany nazwy
            const eventIdentifier = match?.gId ? match : eventName;
            updateEventStaff(date, eventIdentifier, newStaff, calId, true);
        }
    };

    const hiddenStyle = { visibility: "hidden", pointerEvents: "none" };

    return (
        <div className="space-y-2">
            {filteredDates.map((dateStr) => {
                const isWorkDay = employeeDates.includes(dateStr);
                const isDayExpanded = expandedDay === dateStr;
                const shouldHideOriginal = isDayExpanded && !isPortalView;
                const wrapperStyle = shouldHideOriginal ? hiddenStyle : undefined;
                const isExpandedForRender = isPortalView && isDayExpanded;

                // wrapper z ref, żeby była kotwica do scrolla dla wszystkich dni
                if (!isWorkDay) {
                    return (
                        <div
                            key={`free-${dateStr}`}
                            ref={(el) => {
                                if (!rowRefsToUse.current) rowRefsToUse.current = {};
                                rowRefsToUse.current[dateStr] = el;
                            }}
                            data-day-anchor={dateStr}
                            id={`day-${dateStr}`}
                            className={isExpandedForRender ? "day-card-expanded" : ""}
                            style={wrapperStyle}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isExpandedForRender) return;
                                if (onDayClick) onDayClick(dateStr);
                            }}
                        >
                            <FreeDayRow
                                date={new Date(dateStr + "T00:00:00Z")}
                                todayStr={todayStr}
                                employees={employees}
                                unavailable={unavailableByDate?.[dateStr] || []}
                                toggleUnavailable={(emp) => toggleUnavailable(dateStr, emp)}
                                isExpanded={isExpandedForRender}
                            />
                        </div>
                    );
                }

                const eventsForDay = (eventsByDate[dateStr] || []).filter((ev) =>
                    (ev.staff || []).some((s) => nameOf(s) === selName)
                );

                return (
                    <div
                        key={dateStr}
                        ref={(el) => {
                            if (!rowRefsToUse.current) rowRefsToUse.current = {};
                            rowRefsToUse.current[dateStr] = el;
                        }}
                        data-day-anchor={dateStr}
                        id={`day-${dateStr}`}
                        className={isExpandedForRender ? "day-card-expanded" : ""}
                        style={wrapperStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            // W trybie expanded nie wywołuj onDayClick, aby kliknięcie na kartę nie zamykało jej
                            if (isExpandedForRender) return;
                            
                            // Sprawdź, czy kliknięcie było w interaktywny element
                            const target = e.target;
                            const isInteractive = 
                                target.closest('button') || 
                                target.closest('.staff-item') || 
                                target.closest('.event-box') ||
                                target.closest('.add-staff-picker-panel') ||
                                target.closest('[class*="AddStaffPicker"]') ||
                                target.closest('[class*="EventActionsMenu"]') ||
                                target.closest('[class*="StaffMenu"]') ||
                                target.closest('[class*="Droppable"]') ||
                                target.closest('[class*="Draggable"]') ||
                                target.closest('input');
                            
                            // Otwórz powiększenie tylko jeśli kliknięcie było w pustą przestrzeń
                            if (!isInteractive && onDayClick) {
                                onDayClick(dateStr);
                            }
                        }}
                    >
                        <DayRow
                            date={dateStr}
                            todayStr={todayStr}
                            events={eventsForDay}
                            allEventsForDay={
                                typeof allEventsForDate === "function"
                                    ? allEventsForDate(dateStr)
                                    : eventsByDate[dateStr] || []
                            }
                            employees={employees}
                            updateEventStaff={isAdmin ? updateWithCalendarId : () => { }}
                            updateEventColor={isAdmin ? updateEventColor : () => { }}
                            columnAssignments={employeeColumnAssignments}
                            showColorPicker={showColorPicker}
                            setShowColorPicker={setShowColorPicker}
                            unavailable={unavailableByDate?.[dateStr] || []}
                            toggleUnavailable={(emp) => toggleUnavailable(dateStr, emp)}
                            forcedWidth={maxWidth > 0 ? `${maxWidth}px` : "auto"}
                            isAdmin={isAdmin}
                            copyStaffToAllDates={isAdmin ? copyStaffToAllDates : () => { }}
                            updateNeededForEvent={isAdmin ? updateNeededForEvent : () => { }}
                            getAssignedNamesForDate={safeGetAssigned}
                            getUnavailableNamesForDate={safeGetUnavailable}
                            activeCalendarId={null}      // w tym widoku bierzemy ID z eventu
                            eventsByDateFull={eventsByDateFull || eventsByDate}
                            unavailableByDate={unavailableByDate}
                            dragScale={dragScale}
                            isExpanded={isExpandedForRender}
                            layoutFrozen={shouldHideOriginal}
                            onCloseExpanded={onCloseExpanded}
                        />
                    </div>
                );
            })}
        </div>
    );
}
